import { Autocomplete } from '@material-ui/lab';
import React, { useEffect, useState } from 'react';
import {
    InputAdornment,
    SvgIcon,
    TextField,
    useTheme,
} from '@material-ui/core';
import { Search } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useAuth from 'src/hooks/useAuth';
import { READ_PERMISSION } from 'src/constants/permissions';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    DASHBOARD_MENU,
    CONVERSATIONS_MENU,
    MANAGEMENT_MENU,
    LEAD_SETTINGS,
    MARKETING_SETTINGS,
    RECORDINGS_SETTINGS,
    SERVICE_SETTINGS,
    SYSTEM_SETTINGS,
} from 'src/components/MenuWarbox/constants';
import {
    CHARTS_ITEMS,
    DRILLDOWN_ITEMS,
    TABLE_ITEMS,
} from 'src/components/MenuWarbox/constants/reports';
import { TMenuLeaf } from 'src/components/MenuWarbox/components/types';

const SEARCHABLE_ITEMS = [
    {
        title: 'Navbar.Dashboard',
        items: DASHBOARD_MENU,
    },
    {
        title: 'Navbar.Conversations',
        items: CONVERSATIONS_MENU,
    },
    {
        title: 'Navbar.Principal',
        items: MANAGEMENT_MENU,
    },
    {
        title: 'Navbar.Reports',
        items: TABLE_ITEMS,
    },
    {
        title: 'Navbar.Charts',
        items: CHARTS_ITEMS,
    },
    {
        title: 'Navbar.Drilldown',
        items: DRILLDOWN_ITEMS,
    },
    {
        title: 'Navbar.SettingLeads',
        items: LEAD_SETTINGS,
    },
    {
        title: 'Navbar.Marketing',
        items: MARKETING_SETTINGS,
    },
    {
        title: 'Navbar.Recordings',
        items: RECORDINGS_SETTINGS,
    },
    {
        title: 'Navbar.Service',
        items: SERVICE_SETTINGS,
    },
    {
        title: 'Navbar.System',
        items: SYSTEM_SETTINGS,
    },
];

export const SearchBar = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const history = useHistory();

    const [filteredItems, setFilteredItems] = useState<
        (TMenuLeaf & { parentTitle: string })[]
    >([]);
    const [value, setValue] = useState('');
    const theme = useTheme();

    useEffect(() => {
        if (!user?._id) return;

        let itemsAux = [];

        SEARCHABLE_ITEMS.forEach((item) => {
            item.items?.forEach((subItem) => {
                if (
                    isPermissionGranted({
                        permissions: user.permissions || {},
                        permission: subItem.permission,
                        crud: READ_PERMISSION,
                    })
                ) {
                    const response = { ...subItem, parentTitle: item.title };
                    itemsAux.push(response);
                    return true;
                }
            });
        });

        setFilteredItems(itemsAux);
    }, [user]);

    const goToItem = (item?: TMenuLeaf) => {
        if (!item?.path) return;

        setValue('');
        history.push(item.path);
    };

    const handleEnter = () => {
        const item = filteredItems.find(
            (x) => t(x.title).toLowerCase() === value.trim().toLowerCase()
        );

        goToItem(item);
    };

    return (
        <Autocomplete
            freeSolo
            options={filteredItems}
            getOptionLabel={(option) =>
                typeof option === 'string'
                    ? option
                    : t(option.parentTitle) + ' - ' + t(option.title)
            }
            inputValue={value}
            onInputChange={(_, newValue) => {
                setValue(newValue);
            }}
            onChange={(_, selectedItem) => {
                if (typeof selectedItem !== 'string') {
                    goToItem(selectedItem);
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t('SearchRoute')}
                    style={{
                        width: 500,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 10,
                    }}
                    inputProps={{
                        ...params.inputProps,
                        style: {
                            padding: 8,
                        },
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleEnter();
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <InputAdornment position="start">
                                    <SvgIcon fontSize="small" color="action">
                                        <Search />
                                    </SvgIcon>
                                </InputAdornment>

                                {params.InputProps.startAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};
